import React from 'react'

import { InvoiceActivity } from '../../types'
import InvoiceMetaCardLoading from './invoiceMetaCardLoading'
import Tooltip from '../ui/tooltip'
import './sentToClientToggle.scss'

type Props = {
  isSent: boolean
  isSending?: boolean
  disabled?: boolean
  loading?: boolean
  lastActivity?: InvoiceActivity
  sentManually?: boolean
  helperText: string
  manualHint?: string
  tooltipMessage?: string
  onToggle: () => void
}

type ChipToggleProps = {
  isSent: boolean
  isSending: boolean
  isInteractive: boolean
  onToggle: () => void
}

const DeliveryChipToggle: React.FC<ChipToggleProps> = ({
  isSent,
  isSending,
  isInteractive,
  onToggle,
}) => {
  const notSentActive = !isSent && !isSending
  const sentActive = isSent || isSending

  const handleNotSentClick = () => {
    if (!isInteractive || notSentActive) return
    onToggle()
  }

  const handleSentClick = () => {
    if (!isInteractive || sentActive) return
    onToggle()
  }

  const notSentClassName = [
    'invoice-sent-card__chip',
    notSentActive ? 'invoice-sent-card__chip--active invoice-sent-card__chip--not-sent' : '',
    isInteractive ? 'invoice-sent-card__chip--clickable' : '',
  ].filter(Boolean).join(' ')

  const sentClassName = [
    'invoice-sent-card__chip',
    sentActive ? 'invoice-sent-card__chip--active invoice-sent-card__chip--sent' : '',
    isSending ? 'invoice-sent-card__chip--sending' : '',
    isInteractive ? 'invoice-sent-card__chip--clickable' : '',
  ].filter(Boolean).join(' ')

  return (
    <div
      className={[
        'invoice-sent-card__chip-toggle',
        isSending ? 'invoice-sent-card__chip-toggle--sending' : '',
      ].filter(Boolean).join(' ')}
      role='group'
      aria-label='Delivery status'
    >
      <button
        type='button'
        className={notSentClassName}
        disabled={!isInteractive || notSentActive}
        aria-pressed={notSentActive}
        onClick={handleNotSentClick}
      >
        Not sent
      </button>

      <label
        className={[
          'invoice-sent-card__switch',
          !isInteractive ? 'invoice-sent-card__switch--disabled' : '',
        ].filter(Boolean).join(' ')}
      >
        <input
          type='checkbox'
          className='invoice-sent-card__switch-input'
          checked={isSent || isSending}
          disabled={!isInteractive}
          aria-label='Sent to client'
          onChange={() => {
            if (isInteractive) onToggle()
          }}
        />
        <span className='invoice-sent-card__switch-slider' aria-hidden='true' />
      </label>

      <button
        type='button'
        className={sentClassName}
        disabled={!isInteractive || sentActive}
        aria-pressed={sentActive}
        onClick={handleSentClick}
      >
        {isSending ? 'Sending…' : 'Sent'}
      </button>
    </div>
  )
}

const SentToClientToggle: React.FC<Props> = ({
  isSent,
  isSending = false,
  disabled = false,
  loading = false,
  lastActivity,
  helperText,
  manualHint,
  tooltipMessage,
  onToggle,
}) => {
  if (loading) {
    const loadingClassName = [
      'invoice-sent-card',
      isSent ? 'invoice-sent-card--sent' : 'invoice-sent-card--not-sent',
      lastActivity === InvoiceActivity.EMAIL_FAILED ? 'invoice-sent-card--failed' : '',
    ].filter(Boolean).join(' ')
    return (
      <InvoiceMetaCardLoading
        message='Updating delivery'
        className={loadingClassName}
      />
    )
  }

  const isInteractive = !disabled && !isSending
  const isEmailFailed = lastActivity === InvoiceActivity.EMAIL_FAILED

  const handleToggle = () => {
    if (isInteractive) onToggle()
  }

  return (
    <div
      className={[
        'invoice-meta-card',
        'invoice-sent-card',
        isSent ? 'invoice-sent-card--sent' : 'invoice-sent-card--not-sent',
        isEmailFailed ? 'invoice-sent-card--failed' : '',
        isSending ? 'invoice-sent-card--sending' : '',
        isInteractive ? 'invoice-sent-card--interactive' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className='invoice-meta-card__copy mb-2'>
        <span className='invoice-meta-card__title'>Delivery</span>
        <small className='help-tip-text'>{` ${helperText}`}</small>
        {tooltipMessage && <Tooltip message={tooltipMessage} />}
        {manualHint && isInteractive && (
          <p className='invoice-meta-card__hint mb-0 mt-1'>{manualHint}</p>
        )}
      </div>

      <div className='invoice-sent-card__chip-toggle-wrap'>
        <DeliveryChipToggle
          isSent={isSent}
          isSending={isSending}
          isInteractive={isInteractive}
          onToggle={handleToggle}
        />
      </div>
    </div>
  )
}

export default SentToClientToggle
